/**
 * CreateTransactionSlideOver — "New transaction" form panel.
 *
 * Status:
 *   - Amount* · Date* · Party picker* · Description
 *   - Category select · Payment method select · Reference number
 *   - `workflowStatus` is computed from caller role: SA / Admin /
 *     Manager / Accountant → `posted` (lands in studio totals
 *     immediately); Site Engineer / Supervisor → `pending_approval`
 *     (lands in the approval queue). Mirrors mobile's
 *     `add-transaction.tsx:454` so both surfaces enforce the same
 *     approval pipeline. Without this gate, a submit-only role
 *     using the web client could bypass approval entirely (the
 *     Firestore rule also enforces it as a server-side backstop).
 *   - Writes via `createTransaction` / `createStaffAdvance` /
 *     `addAdvanceSettlement` (web SDK).
 *
 * Deferred:
 *   - Receipt photo upload (up to 4)
 *   - "Pending approval" toast when caller is submit-only
 */
import { useEffect, useMemo, useState } from 'react';

import { canFinance } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { CategorySelectWithAdd } from '@/components/CategorySelectWithAdd';
import { PartyPickerSlideOver } from '@/components/PartyPickerSlideOver';
import {
  partyTypeColors,
  partyTypeLabel,
  type PartyType,
} from '@/lib/parties';
import {
  PAYMENT_METHOD_OPTIONS,
  TRANSACTION_CATEGORY_OPTIONS_BY_TYPE,
  addAdvanceSettlement,
  computeAdvanceBalance,
  createStaffAdvance,
  createTransaction,
  type PaymentMethod,
  type TransactionCategory,
  type TransactionType,
} from '@/lib/transactions';
import { useAdvanceChildren, usePartyOpenAdvances } from '@/lib/useAdvances';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

const STYLE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const STYLE_LABEL: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--text-secondary)',
};

const STYLE_REQUIRED: React.CSSProperties = { color: 'var(--critical)' };

const STYLE_GROUP: React.CSSProperties = { marginTop: 12 };

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export type CreateTransactionSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** Pre-selected direction; defaults to payment_out. */
  type: TransactionType;
  onCreated?: (txnId: string) => void;
};

export function CreateTransactionSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  type,
  onCreated,
}: CreateTransactionSlideOverProps) {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  // Submit-only roles (Site Engineer / Supervisor) write transactions
  // that land in `pending_approval`; anyone in `canFinance` posts
  // directly to studio totals. This must match mobile
  // `add-transaction.tsx:454` AND the Firestore rule for txn create.
  // Without the gate, a submit-only role using the web client could
  // bypass approval entirely.
  const postsTxnDirectly = canFinance(orgRole);
  const workflowStatusForCreate: 'posted' | 'pending_approval' =
    postsTxnDirectly ? 'posted' : 'pending_approval';

  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayIso());
  /** Party picker writes BOTH partyId AND the canonical partyName
   *  from the picked doc. Mobile parity (always sets partyId) — and
   *  fixes the historical bug where free-text typings produced
   *  "unlinked" transactions invisible to the Party tab. */
  const [pickedParty, setPickedParty] = useState<{
    id: string;
    name: string;
    partyType: PartyType;
  } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TransactionCategory | ''>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Smart-link state: when the picked party has an open advance
  // on this project, the user gets a checkbox to file the new
  // payment_out as a bill against that advance (vs. as a fresh
  // expense). Default ON — matches SAP Concur's Pattern B. The
  // user can uncheck for vendor-direct payments.
  const [linkToAdvance, setLinkToAdvance] = useState(true);

  // Reset on open / when direction changes.
  useEffect(() => {
    if (!open) return;
    setAmount('');
    setDate(todayIso());
    setPickedParty(null);
    setPickerOpen(false);
    setDescription('');
    setCategory('');
    setPaymentMethod('');
    setReferenceNumber('');
    setBusy(false);
    setError(null);
    setLinkToAdvance(true);
  }, [open, type]);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const isIn = type === 'payment_in';
  const titleText = isIn ? 'Record Payment In' : 'Record Payment Out';
  const ctaText = isIn ? 'Save Payment In' : 'Save Payment Out';
  const ctaBg = isIn ? 'var(--success)' : 'var(--critical)';

  // ─── Smart-link detection ───
  //
  // For payment_out: detect if the picked party currently holds
  // an open advance on this project. The hook reads from the
  // (orgId, partyId, advanceStatus) index — cheap. If found,
  // we also subscribe to the children so the running-balance
  // preview can show "after this bill" live as the user types
  // the amount.
  //
  // For payment_in or no party: returns empty, prompt hides.
  const { advances: partyAdvances } = usePartyOpenAdvances(
    orgId,
    !isIn ? (pickedParty?.id ?? null) : null,
  );
  // First open advance on THIS project (rare: a staff member with
  // 2+ open advances on the same project — picker is overkill, we
  // pick the most recently dated one which `usePartyOpenAdvances`
  // already returns first).
  const openAdvance = useMemo(
    () => partyAdvances.find((a) => a.projectId === projectId) ?? null,
    [partyAdvances, projectId],
  );
  // Subscribe to the parent's children so the preview is live —
  // null `advanceId` keeps the hook idle when no advance is open.
  const { children: advanceChildren } = useAdvanceChildren(
    orgId,
    openAdvance?.id ?? null,
  );
  const liveBalance = useMemo(() => {
    if (!openAdvance) return null;
    return computeAdvanceBalance(
      {
        amount: openAdvance.amount ?? 0,
        advanceStatus: openAdvance.advanceStatus,
      },
      advanceChildren.map((c) => ({
        type: c.type,
        amount: c.amount ?? 0,
        advanceRole: c.advanceRole,
        workflowStatus: c.workflowStatus,
      })),
    );
  }, [openAdvance, advanceChildren]);
  // Only "settlement" makes sense to apply — staff_advance can't
  // nest inside another advance, so we hide the card when the
  // user's category pick is staff_advance (they're creating a
  // NEW advance, not spending one).
  const showSmartLinkCard =
    !!openAdvance && !isIn && category !== 'staff_advance';

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!orgId) return setError('No organisation in scope.');
    if (!user) return setError('You must be signed in.');
    const num = Number(amount.replace(/,/g, ''));
    if (!num || Number.isNaN(num) || num <= 0) {
      return setError('Enter a valid amount greater than zero.');
    }
    if (!date) return setError('Pick a date.');
    if (!pickedParty) return setError('Pick or create a party.');

    setBusy(true);
    try {
      // Three submission paths, picked by category + smart-link:
      //   1. Staff advance creation — `createStaffAdvance`
      //   2. Bill against an open advance (smart-link checked) —
      //      `addAdvanceSettlement` — same atomic write the
      //      "Add bill" button in the advance detail uses, so
      //      the parent recompute happens identically.
      //   3. Fresh standalone payment — `createTransaction`
      const isAdvanceCreate =
        category === 'staff_advance' && type === 'payment_out';
      const isAdvanceBill =
        showSmartLinkCard && linkToAdvance && !!openAdvance;
      let id: string;
      if (isAdvanceCreate) {
        id = await createStaffAdvance({
          orgId,
          projectId,
          staffPartyId: pickedParty.id,
          staffPartyName: pickedParty.name,
          amount: num,
          description: description.trim() || undefined,
          paymentMethod: paymentMethod || undefined,
          date: new Date(`${date}T00:00:00`),
          createdBy: user.uid,
          workflowStatus: workflowStatusForCreate,
        });
      } else if (isAdvanceBill && openAdvance) {
        // Smart-link requires a category — the bill needs to
        // land in a real cost-code bucket on the category report.
        const cat = category || 'others';
        id = await addAdvanceSettlement({
          parentTransactionId: openAdvance.id,
          orgId,
          projectId,
          vendorPartyId: pickedParty.id,
          vendorPartyName: pickedParty.name,
          amount: num,
          category: cat as Exclude<TransactionCategory, 'staff_advance'>,
          description: description.trim() || undefined,
          paymentMethod: paymentMethod || undefined,
          referenceNumber: referenceNumber.trim() || undefined,
          date: new Date(`${date}T00:00:00`),
          createdBy: user.uid,
          workflowStatus: workflowStatusForCreate,
        });
      } else {
        id = await createTransaction({
          orgId,
          projectId,
          type,
          amount: num,
          description: description.trim(),
          partyId: pickedParty.id,
          partyName: pickedParty.name,
          category: category || undefined,
          paymentMethod: paymentMethod || undefined,
          referenceNumber: referenceNumber.trim() || undefined,
          status: 'paid',
          date: new Date(`${date}T00:00:00`),
          createdBy: user.uid,
          workflowStatus: workflowStatusForCreate,
        });
      }
      onCreated?.(id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save transaction.');
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label={titleText}
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">{titleText}</h2>
          <button
            type="button"
            className="slide-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div>
              <label style={STYLE_LABEL}>
                Amount <span style={STYLE_REQUIRED}>*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                style={{ ...STYLE_INPUT, fontSize: 18, fontWeight: 600 }}
                value={amount}
                onChange={(e) => setAmount(e.currentTarget.value.replace(/[^\d.]/g, ''))}
                disabled={busy}
                required
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>
                  Date <span style={STYLE_REQUIRED}>*</span>
                </label>
                <input
                  type="date"
                  style={STYLE_INPUT}
                  value={date}
                  onChange={(e) => setDate(e.currentTarget.value)}
                  disabled={busy}
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Payment method</label>
                <select
                  style={STYLE_INPUT}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.currentTarget.value as PaymentMethod | '')}
                  disabled={busy}
                >
                  <option value="">Select method</option>
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={STYLE_LABEL}>
                Party <span style={STYLE_REQUIRED}>*</span>
              </label>
              {pickedParty ? (
                // Selected chip — click to change.
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  disabled={busy}
                  style={{
                    ...STYLE_INPUT,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                  }}
                >
                  <PartyAvatar party={pickedParty} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pickedParty.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {partyTypeLabel(pickedParty.partyType)} ·{' '}
                      <span style={{ color: 'var(--brand)', fontWeight: 600 }}>
                        Change
                      </span>
                    </div>
                  </div>
                  <i
                    className="ph-bold ph-caret-down"
                    style={{ color: 'var(--text-tertiary)' }}
                  />
                </button>
              ) : (
                // Empty picker — click to open.
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  disabled={busy}
                  style={{
                    ...STYLE_INPUT,
                    cursor: 'pointer',
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                  }}
                >
                  <span>
                    <i className="ph ph-magnifying-glass" />{' '}
                    {isIn ? 'Pick who paid you' : 'Pick who you paid'}
                  </span>
                  <i className="ph-bold ph-caret-down" />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                {/* Type-scoped picker — Payment In shows client-payment
                    milestones (Token, Design Advance, 3D Render Advance,
                    Site Mobilization, Milestone, Final, Others) PLUS any
                    custom categories the studio added for `payment_in`.
                    Payment Out shows the expense set + customs for
                    `payment_out`. "+ Add new category" lets the user
                    extend either list inline. */}
                <CategorySelectWithAdd
                  orgId={orgId ?? null}
                  scope={type}
                  builtIn={TRANSACTION_CATEGORY_OPTIONS_BY_TYPE[type]}
                  value={category}
                  onChange={(v) => setCategory(v as TransactionCategory | '')}
                  allowEmpty
                  label="Category"
                  selectStyle={STYLE_INPUT}
                  disabled={busy}
                />
                {category === 'staff_advance' ? (
                  <div
                    style={{
                      marginTop: 6,
                      padding: '8px 10px',
                      background: '#FEF3C7',
                      border: '1px solid #FDE68A',
                      borderRadius: 6,
                      fontSize: 11,
                      color: '#92400E',
                      lineHeight: 1.4,
                    }}
                  >
                    <i className="ph-bold ph-wallet" style={{ marginRight: 4 }} />
                    Opens an advance wallet. The cash leaves the
                    studio now; you'll file actual bills (vendor +
                    real category) against this advance later.
                  </div>
                ) : null}
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Reference / Bill no.</label>
                <input
                  type="text"
                  placeholder="e.g. INV-1234"
                  style={STYLE_INPUT}
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
            </div>

            {/* Smart-link to advance — Pattern B from the
                industry research. Renders only when the picked
                party has an open advance on this project AND we
                aren't currently creating a new advance. Default
                ON, but the user can uncheck for vendor-direct
                payments. The preview updates live as `amount`
                changes so the user can see exactly what happens
                before they save. */}
            {showSmartLinkCard && openAdvance && liveBalance ? (
              <SmartLinkCard
                checked={linkToAdvance}
                onChange={setLinkToAdvance}
                advanceAmount={openAdvance.amount ?? 0}
                staffName={openAdvance.partyName ?? 'Staff'}
                spentSoFar={liveBalance.spent}
                billsCount={advanceChildren.length}
                remaining={liveBalance.balance}
                billAmount={Number(amount.replace(/,/g, '')) || 0}
                disabled={busy}
              />
            ) : null}

            <div style={STYLE_GROUP}>
              <label style={STYLE_LABEL}>Description</label>
              <textarea
                rows={3}
                placeholder="Notes about this transaction"
                style={{ ...STYLE_INPUT, resize: 'vertical' }}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                padding: 10,
                background: '#FAFAFA',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              📎 Receipt photo upload (up to 4) ships in the next iteration. For now, save and add the receipt from the detail screen later.
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 6,
                paddingTop: 16,
                borderTop: '1px solid var(--border)',
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={handleClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  background: ctaBg,
                  borderColor: ctaBg,
                }}
                disabled={busy || !orgId}
              >
                {busy ? 'Saving…' : ctaText}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Party picker — opens on click of the party row. Always sets
          partyId on save (no free-text fallback). */}
      <PartyPickerSlideOver
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        orgId={orgId}
        defaultName=""
        onPick={(p) => {
          setPickedParty(p);
          setPickerOpen(false);
        }}
      />
    </>
  );
}

// Small avatar used inside the picker chip.
function PartyAvatar({
  party,
}: {
  party: { name: string; partyType: PartyType };
}) {
  const c = partyTypeColors(party.partyType);
  const parts = party.name.trim().split(/\s+/).slice(0, 2);
  const ini = parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: c.bg,
        color: c.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {ini}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Smart-link card — surfaces when the picked party has an open
// advance on this project. Pattern B (auto-prompt, default ON)
// from the SAP Concur playbook. Six refinements baked in:
//   1. Scope match — only shows for THIS project's advance
//   2. Running balance preview, live as user types
//   3. Overshoot handled — shows the reimbursement split
//   4. Always rendered when applicable, predictable surface
//   5. Unapply is on the advance detail side (separate UI)
//   6. Not required — uncheck for vendor-direct payments
// ─────────────────────────────────────────────────────────────

function SmartLinkCard({
  checked,
  onChange,
  advanceAmount,
  staffName,
  spentSoFar,
  billsCount,
  remaining,
  billAmount,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  advanceAmount: number;
  staffName: string;
  spentSoFar: number;
  billsCount: number;
  remaining: number;
  billAmount: number;
  disabled: boolean;
}) {
  const fmt = (n: number) =>
    `₹${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;

  // Preview the wallet state AFTER this bill is filed. The same
  // math closeAdvance does on settle, run here so the user can
  // see overshoot consequences before submitting.
  let preview: { type: 'fits' | 'overshoot' | 'empty'; afterRemaining: number; excess: number } = {
    type: 'empty',
    afterRemaining: remaining,
    excess: 0,
  };
  if (billAmount > 0) {
    const afterRemaining = remaining - billAmount;
    if (afterRemaining >= 0) {
      preview = { type: 'fits', afterRemaining, excess: 0 };
    } else {
      preview = { type: 'overshoot', afterRemaining: 0, excess: -afterRemaining };
    }
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 8,
        background: checked ? '#FEF3C7' : '#FAFAFA',
        border: '1px solid',
        borderColor: checked ? '#FDE68A' : 'var(--border)',
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.currentTarget.checked)}
          disabled={disabled}
          style={{ marginTop: 3, accentColor: 'var(--brand)' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: checked ? '#92400E' : 'var(--text-primary)',
            }}
          >
            <i className="ph-bold ph-wallet" style={{ marginRight: 6 }} />
            File against {staffName}'s {fmt(advanceAmount)} advance
          </div>
          <div
            style={{
              fontSize: 11,
              color: checked ? '#92400E' : 'var(--text-secondary)',
              marginTop: 2,
            }}
          >
            {checked
              ? 'This bill will reduce the advance wallet. Uncheck for a vendor-direct payment.'
              : 'Will be recorded as a fresh expense (the advance stays untouched).'}
          </div>
        </div>
      </label>

      {/* Balance breakdown — only shown when the link is active */}
      {checked ? (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid #FDE68A',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 12,
          }}
        >
          <BalanceRow label="Given" value={fmt(advanceAmount)} />
          <BalanceRow
            label={`Filed (${billsCount} bill${billsCount === 1 ? '' : 's'})`}
            value={fmt(spentSoFar)}
          />
          <BalanceRow
            label="Remaining"
            value={fmt(remaining)}
            emphasized
          />

          {preview.type === 'fits' ? (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                background: '#fff',
                borderRadius: 6,
                color: '#15803D',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <i className="ph-bold ph-check-circle" />
              After this {fmt(billAmount)}: {fmt(preview.afterRemaining)} left in wallet
            </div>
          ) : null}

          {preview.type === 'overshoot' ? (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                background: '#fff',
                borderRadius: 6,
                color: 'var(--critical)',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                <i className="ph-bold ph-warning-circle" /> After this {fmt(billAmount)}:
              </div>
              <div>
                · {fmt(remaining)} reduces wallet to ₹0
              </div>
              <div>
                · {fmt(preview.excess)} reimbursable to {staffName} when
                you close the advance
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function BalanceRow({
  label,
  value,
  emphasized,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '2px 0',
        fontWeight: emphasized ? 700 : 500,
        color: emphasized ? '#92400E' : 'var(--text-secondary)',
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
